import { AuthorizedContent } from 'modules/auth';
import { toDateString } from 'modules/data';
import { SingleValueWithForm } from 'modules/forms';
import { useI18n } from 'modules/i18n';
import { SelectedRegistryReferencesContainer } from 'modules/registry-references';
import { formatDuration } from 'modules/utils';
import PropTypes from 'prop-types';

import InterviewCollectionInfo from './InterviewCollectionInfo';

export default function InterviewInfo({ interview, languages }) {
    const { t, locale } = useI18n();

    if (!interview?.language_id) {
        return null;
    }

    return (
        <div>
            <SingleValueWithForm
                obj={interview}
                attribute="archive_id"
                value={interview.archive_id}
                readOnly
                hideEmpty
            />
            <SingleValueWithForm
                obj={interview}
                attribute={'signature_original'}
                value={interview.signature_original}
                hideEmpty
            />
            <SingleValueWithForm
                obj={interview}
                attribute={'interview_date'}
                value={toDateString(interview.interview_date, locale)}
                editValue={interview.interview_date}
                help={'help_texts.date_field'}
                hideEmpty
            />
            <SingleValueWithForm
                obj={interview}
                attribute={'publication_date'}
                value={interview.publication_date}
                validate={function (v) {
                    return /^\d{4}$/.test(v);
                }}
                hideEmpty
            />
            <SingleValueWithForm
                obj={interview}
                attribute={'description'}
                value={interview.description?.[locale]}
                elementType="textarea"
                multiLocale
                linkUrls
                hideEmpty
                //collapse
            />
            <SingleValueWithForm
                obj={interview}
                attribute={'notes'}
                value={interview.notes?.[locale]}
                elementType="textarea"
                multiLocale
                linkUrls
                extraStatusCheckboxes={[
                    {
                        attribute: 'include_notes_in_transcript_pdf',
                        value: !!interview.include_notes_in_transcript_pdf,
                        labelKey:
                            'activerecord.attributes.interview.include_notes_in_transcript_pdf',
                    },
                ]}
                hideEmpty
            />
            <SingleValueWithForm
                obj={interview}
                optionsScope={'search_facets'}
                elementType={'select'}
                values={['video', 'audio']}
                value={t(`search_facets.${interview.media_type}`)}
                attribute={'media_type'}
            />
            <SingleValueWithForm
                obj={interview}
                attribute="media_missing"
                value={t(`boolean_value.${interview.media_missing}`)}
                elementType="input"
                type="checkbox"
                noStatusCheckbox
                hideEmpty
            />
            <SingleValueWithForm
                obj={interview}
                validate={function (v) {
                    return /^\d{2}:\d{2}:\d{2}\.\d{3}$/.test(v);
                }}
                attribute={'duration'}
                individualErrorMsg={'format'}
                value={formatDuration(interview.duration)}
                hideEmpty
            />
            <SingleValueWithForm
                obj={interview}
                validate={function (v) {
                    return /^\d+$/.test(v);
                }}
                attribute={'tape_count'}
                value={interview.tape_count}
                hideEmpty
            />
            <SingleValueWithForm
                obj={interview}
                elementType="input"
                type="checkbox"
                attribute={'transcript_coupled'}
                value={t(`boolean_value.${interview.transcript_coupled}`)}
                hideEmpty
            />
            {[
                'primary_language_id',
                'secondary_language_id',
                'primary_translation_language_id',
                'secondary_translation_language_id',
            ].map((attribute) => (
                <SingleValueWithForm
                    key={attribute}
                    elementType={'select'}
                    obj={interview}
                    values={languages}
                    withEmpty={true}
                    //validate={function(v){return /^\d+$/.test(v)}}
                    attribute={attribute}
                    value={languages[interview[attribute]]?.name[locale]}
                    hideEmpty
                />
            ))}

            <InterviewCollectionInfo interview={interview} />

            <AuthorizedContent object={interview} action="update">
                <SingleValueWithForm
                    elementType={'select'}
                    obj={interview}
                    attribute={'workflow_state'}
                    values={['public', 'restricted', 'unshared']}
                    value={t(`workflow_states.${interview.workflow_state}`)}
                    optionsScope={'workflow_states'}
                    noStatusCheckbox
                />
            </AuthorizedContent>

            <SingleValueWithForm
                obj={interview}
                attribute="startpage_position"
                value={interview.startpage_position}
                noStatusCheckbox
            />

            <SelectedRegistryReferencesContainer refObject={interview} />
        </div>
    );
}

InterviewInfo.propTypes = {
    interview: PropTypes.object.isRequired,
    languages: PropTypes.object.isRequired,
};
